import { getRandomNumbers } from '../utils';
import { createContext, ReactNode, useContext } from 'react';
import {
  Box,
  ChakraProps,
  Container,
  CSSObject,
  Flex,
  Skeleton,
  SkeletonCircle,
  SkeletonProps,
  SkeletonText,
  SkeletonTextProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';

export interface ShimmerContextModel {
  isLoaded: boolean;
  children?: ReactNode;
}

export interface ShimmerProps extends SkeletonProps {
  fallback?: ReactNode;
}

export interface BarChartShimmerProps extends SkeletonProps {
  containerProps?: CSSObject;
  numberOfBars: number;
  barWidth?: ChakraProps['width'];
}

export interface AreaChartShimmerProps extends SkeletonProps {
  graphHeight?: number;
  graphWidth?: number;
  containerProps?: CSSObject;
}

export interface PieChartShimmerProps extends SkeletonProps {
  pieChartRadius?: number;
}

const ShimmerContext = createContext<ShimmerContextModel>({
  isLoaded: true,
});

const useShimmerContext = (): ShimmerContextModel => useContext(ShimmerContext);

const ShimmerProvider = ShimmerContext.Provider;

export type ShimmerWrapperProps = ShimmerContextModel & CSSObject;

export const ShimmerWrapper = (props: ShimmerWrapperProps) => {
  const { isLoaded, children, ...rest } = props;
  return (
    <Box sx={{ ...rest }}>
      <ShimmerProvider value={{ isLoaded }}>{children}</ShimmerProvider>
    </Box>
  );
};

export const Shimmer = (props: ShimmerProps) => {
  const { children, isLoaded, fallback, ...rest } = props;
  const { isLoaded: topLevelLoaded } = useShimmerContext();
  const finalLoaded = isLoaded === undefined ? topLevelLoaded : isLoaded;
  return (
    <>
      {fallback ? (
        finalLoaded ? (
          children
        ) : (
          fallback
        )
      ) : (
        <Skeleton
          isLoaded={finalLoaded}
          startColor='gray.100'
          endColor='gray.200'
          {...rest}
        >
          {children}
        </Skeleton>
      )}
    </>
  );
};

const TextShimmer = (props: SkeletonTextProps) => {
  const { children, isLoaded, ...rest } = props;
  const { isLoaded: topLevelLoaded } = useShimmerContext();
  const finalLoaded = isLoaded === undefined ? topLevelLoaded : isLoaded;
  return (
    <>
      {finalLoaded ? (
        children
      ) : (
        <SkeletonText
          isLoaded={finalLoaded}
          startColor='gray.100'
          endColor='gray.200'
          {...rest}
        />
      )}
    </>
  );
};

const CircleShimmer = (props: SkeletonProps) => {
  const { children, isLoaded, ...rest } = props;
  const { isLoaded: topLevelLoaded } = useShimmerContext();
  const finalLoaded = isLoaded === undefined ? topLevelLoaded : isLoaded;
  return (
    <>
      {finalLoaded ? (
        children
      ) : (
        <SkeletonCircle
          isLoaded={finalLoaded}
          startColor='gray.100'
          endColor='gray.200'
          {...rest}
        />
      )}
    </>
  );
};

const BarChartShimmer = (props: BarChartShimmerProps) => {
  const { containerProps, numberOfBars, barWidth = 4, ...rest } = props;
  const randomHeights = getRandomNumbers(numberOfBars);
  const styles = useMultiStyleConfig('Shimmer', props);

  return (
    <Flex sx={{ ...styles.BarChartContainer, ...containerProps }}>
      {randomHeights.map((n, i) => (
        <Shimmer key={i} width={barWidth} height={`${n}%`} {...rest} />
      ))}
    </Flex>
  );
};

const AreaChartShimmer = (props: AreaChartShimmerProps) => {
  const {
    containerProps,
    graphHeight = 200,
    graphWidth = 2000,
    ...rest
  } = props;
  const randomHeights = [graphHeight];
  for (let i = 0; i < graphWidth; i++) {
    randomHeights.push(
      randomHeights[i] +
        (Math.floor(Math.random() * 10) + 1 <= 5 ||
        randomHeights[i] === graphHeight
          ? -1
          : 1),
    );
  }
  const styles = useMultiStyleConfig('Shimmer', props);

  return (
    <Flex sx={{ ...styles.AreaChartContainer, ...containerProps }}>
      {randomHeights.map((n, i) => {
        return <Shimmer key={i} width='1px' height={`${n}px`} {...rest} />;
      })}
    </Flex>
  );
};

const PieChartShimmer = (props: PieChartShimmerProps) => {
  const { pieChartRadius = '125px', ...rest } = props;
  return (
    <Container centerContent {...rest}>
      <Flex direction={'column'}>
        <Flex>
          <Box w={pieChartRadius} h={pieChartRadius} bg={'transparent'} />
          <Shimmer
            w={pieChartRadius}
            h={pieChartRadius}
            borderTopRightRadius={'full'}
          />
        </Flex>
        <Flex>
          <Shimmer
            w={pieChartRadius}
            h={pieChartRadius}
            borderBottomLeftRadius={'full'}
          />
          <Shimmer
            w={pieChartRadius}
            h={pieChartRadius}
            borderBottomRightRadius={'full'}
          />
        </Flex>
      </Flex>
    </Container>
  );
};

Shimmer.Text = TextShimmer;
Shimmer.Circle = CircleShimmer;
Shimmer.BarChart = BarChartShimmer;
Shimmer.AreaChart = AreaChartShimmer;
Shimmer.PieChart = PieChartShimmer;
